import apiClient from '@/services/axios'
import _ from 'lodash'
import { getRoutingSessionId } from '@/services/routing-session'

function withRoutingSession(payload) {
  const sessionId = getRoutingSessionId()
  if (!sessionId) return payload
  return { ...payload, routingSessionId: sessionId }
}

export function getRoutings(query) {
  return apiClient.get(`/routings`, {
    params: query,
  })
}
export function createRouting(routing) {
  return apiClient.post('/routings', routing)
}

// Mercury: start an async optimization job for an org/shift. Returns { jobId }.
export function startMercuryJob(payload) {
  return apiClient.post('/routings/optimize/mercury', payload)
}

// Mercury: pre-build estimate (no solve) for an org/shift/options. Returns the
// wrapped { value, basis, confidence_note } fields per the display contract.
export function estimateMercuryJob(payload) {
  return apiClient.post('/routings/estimate/mercury', payload)
}

// Mercury: poll a job by id for status/results.
export function getMercuryJob(jobId) {
  return apiClient.get(`/routings/optimize/mercury/jobs/${jobId}`)
}

// Mercury: pause/resume/cancel the job on the ENGINE (halts the actual solve,
// not just local polling). action = 'pause' | 'resume' | 'cancel'.
export function controlMercuryJob(jobId, action) {
  return apiClient.post(`/routings/optimize/mercury/jobs/${jobId}/${action}`)
}

// Mercury: the run-audit ledger (past runs for an org, display headline subset).
export function listMercuryRuns(params) {
  return apiClient.get('/routings/optimize/mercury/runs', { params })
}

// Snap/build geometry for routings. onlyMissing=true only regenerates routings
// that have no geometry yet (e.g. freshly created Network Mercury routes).
export function generateRoutingGeometry(payload) {
  return apiClient.post('/routings/generate-geometry', payload)
}

// Network Mercury: persist the engine's own OSRM route line (encoded polyline5)
// as the routing's geometry. Backend decodes + buffers it into the standard
// road polygon — no Mapbox involved.
export function setRoutingGeometry(routingId, polyline) {
  return apiClient.post(`/routings/${routingId}/geometry`, { polyline })
}

// Network Mercury: persist a WHOLE engine solve (routings + stops + student
// assignments + OSRM geometry) in one request — replaces the per-stop chain.
export function persistNetworkMercury(payload) {
  return apiClient.post('/routings/network-mercury', payload)
}

export function updateRouting({ id, ...routing }) {
  return apiClient.patch(
    `/routings/${id}`,
    withRoutingSession(_.omit(routing, 'stops', 'students')),
  )
}

export function getRoutingVersions(id) {
  return apiClient.get(`/routings/${id}/versions`)
}

export function getRoutingVersion(id, versionNumber) {
  return apiClient.get(`/routings/${id}/versions/${versionNumber}`)
}

export function startRoutingEditSession(id, sessionId) {
  return apiClient.post(`/routings/${id}/sessions`, { sessionId })
}

export function getRoutingById(id) {
  return apiClient.get(`/routings/${id}`)
}

// Discard in the route editor: put the route's stops and riders back to the
// snapshot taken when the editor opened (the editor saves each stop change
// as it is made, so closing alone keeps them).
export function discardRoutingEdits(id, snapshot) {
  return apiClient.post(`/routings/${id}/discard-edits`, snapshot)
}

// Weekly schedule (crew + weekday times + exception blocks) for a route.
// Shape: { base: { vehicleId, driverId, assistantIds, days: [...] } | null,
//          exceptions: [same block shape] }
export function getRoutingSchedule(id) {
  return apiClient.get(`/routings/${id}/schedule`)
}

export function upsertRoutingSchedule(id, schedule) {
  return apiClient.put(`/routings/${id}/schedule`, schedule)
}

// keepGroup: leave the routing group in place even if this was its last
// routing (Build Routes replaces runs inside a group it still needs).
export function deleteRouting(id, { keepGroup = false } = {}) {
  return apiClient.delete(
    `/routings/${id}`,
    keepGroup ? { params: { keepGroup: true } } : undefined,
  )
}

export function updateRoutingState({ id, ...routing }) {
  return apiClient.patch(`/routings/state/${id}`, routing)
}
